import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom"; 
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";
import { FaXTwitter, FaYoutube } from "react-icons/fa6";

const { FiWind, FiMail, FiFacebook, FiLinkedin, FiExternalLink } = FiIcons;

export default function Footer() {
  const year = new Date().getFullYear();

  const socialLinks = [
    { icon: FiFacebook, href: "https://www.facebook.com/greengasturbines/", label: "Facebook" },
    { icon: FaXTwitter, href: "https://x.com/GreenGasTs", label: "X (Twitter)" },
    { icon: FaYoutube, href: "https://www.youtube.com/@greengasturbines", label: "YouTube" },
    { icon: FiLinkedin, href: "https://www.linkedin.com/company/green-gas-turbines", label: "LinkedIn" },
  ];

  const tools = [
    { name: "Hydrogen Blend Calculator", href: "/interactive-tools/hydrogen-blend-calculator" },
    { name: "Telemetry Simulator", href: "/interactive-tools/telemetry-simulator" },
    { name: "SCADA Dashboard", href: "/interactive-tools/scada-dashboard" },
    { name: "LCOE Retrofit", href: "/interactive-tools/lcoe-retrofit" },
    { name: "Emissions Estimator", href: "/interactive-tools/emissions-estimator" },
    { name: "Combustion Explorer", href: "/interactive-tools/combustion-explorer" },
    { name: "Peaker Scheduler", href: "/interactive-tools/peaker-scheduler" },
    { name: "H2-Ready Checklist", href: "/interactive-tools/h2-ready-checklist" },
    { name: "Data Center Scenarios", href: "/interactive-tools/datacenter-scenarios" },
    { name: "Alarm Rules", href: "/interactive-tools/alarm-rules" },
    { name: "Anomaly Playground", href: "/interactive-tools/anomaly-playground" },
    { name: "Tag Mapper", href: "/interactive-tools/tag-mapper" },
  ];

  // split into 2 columns of 6
  const colA = tools.slice(0, 6);
  const colB = tools.slice(6, 12);

  return (
    <footer className="bg-secondary-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* 4-column layout - 2 cols on mobile, 4 on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {/* About & Contact - spans full width on mobile */}
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <SafeIcon icon={FiWind} className="h-7 w-7 text-primary-400" />
              <span className="text-2xl font-bold">GreenGasTurbines</span>
            </div>
            <p className="text-gray-300 mb-6 leading-relaxed md:max-w-xs">
              Pioneering <strong>hydrogen-ready</strong> and <strong>RNG-fuelled</strong> gas turbines that cut
              emissions without compromising power or profit.
            </p>
            <div className="flex items-center gap-3 text-gray-300">
              <SafeIcon icon={FiMail} className="h-4 w-4 text-primary-400" />
              <a
                href="mailto:info@greengasturbines.com"
                className="text-sm hover:text-primary-400 transition-colors"
              >
                info@greengasturbines.com
              </a>
            </div>
        {/* Product Hunt badge */}
<div className="mt-4">
  <a
    href="https://www.producthunt.com/products/green-gas-turbines?embed=true&utm_source=badge-featured&utm_medium=badge"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="View Green Gas Turbines on Product Hunt"
  >
    <img
      src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1004756&theme=dark"
      alt="Green Gas Turbines – Hydrogen-ready low-carbon power | Product Hunt"
      width={250}
      height={54}
      loading="lazy"
      className="h-auto w-[250px] max-w-full"
    />
  </a>
</div>

          </div>

          {/* Resources */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Resources</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/blog" className="text-gray-300 hover:text-primary-400 text-sm transition-colors">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/interactive-tools" className="text-gray-300 hover:text-primary-400 text-sm transition-colors">
                  Interactive Tools
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-gray-300 hover:text-primary-400 text-sm transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-primary-400 text-sm transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Popular Tools */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Popular Tools</h3>
            <ul className="space-y-3">
              {colA.slice(0, 6).map(({ name, href }) => (
                <li key={href}>
                  <Link
                    to={href}
                    className="text-gray-300 hover:text-primary-400 text-sm transition-colors"
                  >
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Follow Us */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Follow Us</h3>
            <p className="text-gray-300 text-sm mb-6 md:max-w-xs">
              Join the conversation on green energy and stay up to date with product releases.
            </p>
            <div className="flex gap-4">
              {socialLinks.map(({ icon, href, label }) => (
                <motion.a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-10 h-10 bg-secondary-800 rounded-lg flex items-center justify-center hover:bg-primary-600 transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <SafeIcon icon={icon} className="h-5 w-5" />
                </motion.a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-secondary-800 mt-12 pt-6 text-center text-gray-400 text-sm space-y-3">
          <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center sm:flex-wrap">
            <span>© {year} GreenGasTurbines. All rights reserved.</span>
            <span className="hidden sm:inline px-2">|</span>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
              <Link to="/privacy-policy" className="hover:text-primary-400">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-primary-400">Terms of Service</Link>
              <Link to="/cookie-policy" className="hover:text-primary-400">Cookie Policy</Link>
            </div>
          </div>
          <div>
            Website by{" "}
            <a
              href="https://www.ravensdale.co.za"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-primary-400"
            >
              Ravensdale Digital
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}